import React from "react";
import { Input } from "../../ui/input";

// Percent for a tech not yet in the map defaults to the equal share.
const pctOf = (map, id, eq) => {
  const v = parseFloat(map?.[id]);
  return isNaN(v) ? eq : v;
};
const amtOf = (map, id) => {
  const v = parseFloat(map?.[id]);
  return isNaN(v) ? 0 : v;
};

/**
 * Commission split editor shown when a job has 2+ assigned technicians.
 * Equal (default) or Custom — Custom splits by % of the total tech
 * commission, or by fixed $ (additional techs get their $, primary keeps
 * the remainder). Backend contract: split_mode + split_unit + splits.
 */
export const TechSplitEditor = ({ techs, mode, unit = "percent", percents, amounts, onChange, testIdPrefix = "tech-split" }) => {
  if (!techs || techs.length < 2) return null;
  const eq = Math.round((100 / techs.length) * 100) / 100;
  const isCustom = mode === "custom";
  const isAmount = isCustom && unit === "amount";
  const total = techs.reduce((s, t) => s + pctOf(percents, t.id, eq), 0);
  const totalOk = Math.abs(total - 100) < 0.5;
  const amountTotal = techs.filter((t) => !t.primary).reduce((s, t) => s + amtOf(amounts, t.id), 0);

  const emit = (patch) => onChange({ mode, unit, percents: percents || {}, amounts: amounts || {}, ...patch });

  const setMode = (m) => {
    if (m === "custom") {
      const map = {};
      techs.forEach((t) => { map[t.id] = pctOf(percents, t.id, eq); });
      emit({ mode: "custom", percents: map });
    } else {
      emit({ mode: "equal", unit: "percent", percents: {}, amounts: {} });
    }
  };

  return (
    <div className="rounded-lg border border-border bg-muted/30 p-2.5 space-y-2" data-testid={`${testIdPrefix}-editor`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-xs font-medium text-muted-foreground">Commission split</span>
        <div className="flex items-center gap-1.5">
          <div className="flex rounded-md border border-border overflow-hidden text-xs">
            <button type="button"
              onClick={() => setMode("equal")}
              className={`px-2.5 py-1 transition-colors ${!isCustom ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted/60"}`}
              data-testid={`${testIdPrefix}-equal-btn`}
            >
              Equal
            </button>
            <button type="button"
              onClick={() => setMode("custom")}
              className={`px-2.5 py-1 transition-colors border-l border-border ${isCustom ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted/60"}`}
              data-testid={`${testIdPrefix}-custom-btn`}
            >
              Custom
            </button>
          </div>
          {isCustom && (
            <div className="flex rounded-md border border-border overflow-hidden text-xs">
              <button type="button"
                onClick={() => emit({ unit: "percent" })}
                className={`px-2 py-1 transition-colors ${!isAmount ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted/60"}`}
                data-testid={`${testIdPrefix}-unit-percent-btn`}
              >
                %
              </button>
              <button type="button"
                onClick={() => emit({ unit: "amount" })}
                className={`px-2 py-1 transition-colors border-l border-border ${isAmount ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted/60"}`}
                data-testid={`${testIdPrefix}-unit-amount-btn`}
              >
                $
              </button>
            </div>
          )}
        </div>
      </div>

      {!isCustom ? (
        <p className="text-[11px] text-muted-foreground" data-testid={`${testIdPrefix}-equal-hint`}>
          Split equally between all {techs.length} technicians — {eq}% each.
        </p>
      ) : isAmount ? (
        <div className="space-y-1.5">
          {techs.map((t) => (
            <div key={t.id} className="flex items-center gap-2">
              <span className="flex-1 text-sm text-foreground truncate">
                {t.name}{t.primary ? <span className="text-[10px] text-muted-foreground ml-1">(primary)</span> : null}
              </span>
              {t.primary ? (
                <span className="text-xs text-muted-foreground italic w-24 text-right" data-testid={`${testIdPrefix}-remainder-${t.id}`}>
                  Remainder
                </span>
              ) : (
                <div className="relative w-24">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">$</span>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={amounts?.[t.id] ?? ""}
                    placeholder="0.00"
                    onChange={(e) => emit({ amounts: { ...amounts, [t.id]: e.target.value } })}
                    className="h-8 text-sm pl-5 bg-background"
                    data-testid={`${testIdPrefix}-amt-${t.id}`}
                  />
                </div>
              )}
            </div>
          ))}
          <p className="text-[11px] text-right text-muted-foreground" data-testid={`${testIdPrefix}-amount-hint`}>
            ${Math.round(amountTotal * 100) / 100} fixed — primary tech keeps the remaining commission
          </p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {techs.map((t) => (
            <div key={t.id} className="flex items-center gap-2">
              <span className="flex-1 text-sm text-foreground truncate">
                {t.name}{t.primary ? <span className="text-[10px] text-muted-foreground ml-1">(primary)</span> : null}
              </span>
              <div className="relative w-24">
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={percents?.[t.id] ?? eq}
                  onChange={(e) => emit({ percents: { ...percents, [t.id]: e.target.value } })}
                  className="h-8 text-sm pr-6 bg-background"
                  data-testid={`${testIdPrefix}-pct-${t.id}`}
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">%</span>
              </div>
            </div>
          ))}
          <div className={`text-[11px] text-right font-medium ${totalOk ? "text-emerald-600 dark:text-emerald-400" : "text-red-500"}`} data-testid={`${testIdPrefix}-total`}>
            Total: {Math.round(total * 100) / 100}% {totalOk ? "" : "— must equal 100%"}
          </div>
        </div>
      )}
    </div>
  );
};

export default TechSplitEditor;
